import { PRIVACY_CLASSNAMES } from "@/lib/analytics/shared";
import { classNames } from "@/lib/utils";
import IconButton from "@components/library/IconButton";
import { hlToast, ToastVariant } from "@components/library/Toast";
import { ExclamationCircleIcon } from "@heroicons/react/solid";
import { EditorProps, OnMount } from "@monaco-editor/react";
import { Copy } from "@phosphor-icons/react";
import Monaco from "monaco-editor/esm/vs/editor/editor.api";
import { useCallback, useEffect, useRef } from "react";
import CodeEditor from "./CodeEditor";

export interface JSONEditorProps {
  defaultValue: string;
  onChange?: (prevState?: string) => void;
  error?: any | null;
  editorProps?: Omit<EditorProps, "options" | "onMount" | "onChange" | "defaultValue">;
  /* A simple hack on top to refresh the content when this value changes  */
  triggerRefresh?: any;
  loading?: {
    loading: boolean;
    loadingText?: string;
  };
  options?: {
    lineNumbers?: boolean;
    focusOnMount?: boolean;
    noIndentationGuides?: boolean;
    autosize?: boolean;
  };
  onMount?: OnMount;
  showCopyButton?: boolean;
}

const JSONEditor = ({
  defaultValue,
  onChange,
  error,
  editorProps,
  triggerRefresh,
  options,
  loading,
  onMount: onMountProp,
  showCopyButton = true,
}: JSONEditorProps) => {
  const monacoRef = useRef<typeof Monaco | null>(null);
  const editorRef = useRef<Monaco.editor.IStandaloneCodeEditor | null>(null);

  /** JSON Editor that resizes to fit the content.
   *
   * See: https://github.com/microsoft/monaco-editor/issues/794#issuecomment-1384994230
   */
  const containerRef = useRef<HTMLDivElement>(null);
  const onMount = useCallback(
    (editor: Monaco.editor.IStandaloneCodeEditor, monaco: any) => {
      if (options?.autosize) {
        const updateHeight = () => {
          const contentHeight = Math.min(200, editor.getContentHeight());
          const width = containerRef.current?.clientWidth ?? 300;
          if (containerRef.current) {
            editor.layout({ width, height: contentHeight });
          }
        };
        editor.onDidContentSizeChange(updateHeight);
        updateHeight();
      }

      onMountProp?.(editor, monaco);
    },
    [onMountProp, options?.autosize],
  );

  useEffect(() => {
    const monacoInstance = monacoRef.current;
    const editorInstance = editorRef.current;

    const model = editorInstance?.getModel();
    if (editorInstance !== null && monacoInstance != null && model) {
      const editor = monacoInstance.editor;
      const detail = error?.detail;
      const highlight: { line: number; character: number } | undefined = detail?.body?.highlight;
      if (highlight !== undefined) {
        editor.setModelMarkers(model, "llama", [
          {
            startLineNumber: highlight.line,
            startColumn: highlight.character || 0,
            endLineNumber: highlight.line,
            endColumn: highlight.character + 1,
            message: detail?.message,
            severity: monacoInstance.MarkerSeverity.Error,
          },
        ]);
      } else {
        editor.setModelMarkers(model, "llama", []);
      }
    }
  }, [error?.detail]);

  useEffect(() => {
    const editorInstance = editorRef.current;
    if (editorInstance !== null) {
      editorInstance.setValue(defaultValue || ""); // Set the editor's value to defaultValue or an empty string if it's null or undefined
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [triggerRefresh]);

  return (
    <div className={classNames("flex h-full flex-col", options?.autosize ? "max-h-[300px]" : "")} ref={containerRef}>
      <div className="flex grow flex-col overflow-hidden rounded-lg bg-oldgray-100">
        <div className={classNames("group relative min-h-0 shrink grow ", PRIVACY_CLASSNAMES["high"])}>
          {showCopyButton && (
            <div className="absolute right-8 top-8 z-10 flex gap-4 opacity-0 transition-opacity duration-200 group-hover:opacity-100">
              <IconButton
                tooltip="Copy snippet to clipboard"
                onClick={() => {
                  navigator.clipboard.writeText(editorRef.current?.getValue() || "");
                  hlToast({ variant: ToastVariant.Success, title: "Copied to clipboard!" });
                }}
                Icon={Copy}
                shade="gray"
                styling="solid"
                elevated
              />
            </div>
          )}
          <CodeEditor
            {...editorProps}
            defaultLanguage="json"
            options={{
              lineNumbers: options?.lineNumbers ? "on" : "off",
              wordWrap: "on",
              readOnly: onChange === undefined,
              padding: { top: 4, bottom: 4 },
              guides: {
                indentation: options?.noIndentationGuides ? false : true,
              },
            }}
            onMount={(editor, monaco) => {
              editorRef.current = editor;
              monacoRef.current = monaco;

              if (onMount) {
                onMount(editor, monaco);
              }
              options?.focusOnMount ? editor.focus() : null;
            }}
            onChange={onChange}
            defaultValue={defaultValue}
            loading={loading?.loading}
            loadingText={loading?.loadingText}
          />
        </div>
        {error && (
          <div className="flex w-full items-center rounded-sm border-t border-oldgray-300 bg-oldgray-100 bg-opacity-90 p-4 px-8">
            <ExclamationCircleIcon className="mr-6 h-16 w-16 shrink-0 text-oldred-700" />
            <div className="text-11-14 text-oldgray-800">
              {error.detail?.message ? (
                error.detail.message
              ) : (
                <>Error: {JSON.stringify(error.message || error.detail.description)}</>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default JSONEditor;
